import styled from 'styled-components';
import { Point } from 'dragonmouse-shared/geometry';

export const Base = styled.div`
	// layout
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;

	// style
	background-color: ${({ theme }) => theme.gray[0]};
	border-radius: 8px;
	// overflow-y: auto;
	// overflow-x: hidden;
`;

interface JumpButtonProps {
	isMobile?: boolean;
}

export const JumpButton = styled.div<JumpButtonProps>`
	position: absolute;
	top: ${({ isMobile }) => (isMobile ? '-28px' : '-22px')};
	left: 50%;
	transform: translateX(-50%);
	z-index: 2;

	// i want to shift the icon down by 4px while matching height and width
	// so i reduce height by 4px to 22px
	width: ${({ isMobile }) => (isMobile ? '44px' : '26px')};
	height: ${({ isMobile }) => (isMobile ? '40px' : '22px')};
	padding-top: ${({ isMobile }) => (isMobile ? '4px' : '4px')};

	display: flex;
	align-items: center;
	justify-content: center;
	background: ${({ theme }) => theme.gray[0]};
	border: 1px solid ${({ theme }) => theme.gray[400]};
	&:hover {
		background: ${({ theme }) => theme.gray[100]};
	}
	border-radius: 40px;
`;

export const JumpButtonIcon = styled.div`
	// filter out mouse events on the icon, so that all clicks go to the base
	// otherwise Icon will block clicks on Base, and we won't detect when the user clicks the button
	pointer-events: none;
`;

export const InputArea = styled.div`
	position: relative;
`;

export const ScrollingArea = styled.div`
	position: relative;
	overflow-y: auto;
	overflow-x: hidden;
	// make scrollbar a bit wider
	scrollbar-width: thin;
	&::-webkit-scrollbar {
		width: 8px;
	}
	scrollbar-color: ${({ theme }) => theme.gray[400]} ${({ theme }) => theme.gray[100]};
`;

export const Spacer = styled.div`
	flex: 1;
	min-height: 0;
`;

interface MessageProps {
	isMobile?: boolean;
}

export const UserMessage = styled.div<MessageProps>`
	// layout
	width: calc(100% - 68px);
	padding: 10px;
	padding-bottom: 12px;
	padding-left: 14px;
	margin: 8px;
	margin-left: 36px;
	align-self: flex-end;

	// style
	font-size: ${({ isMobile }) => (isMobile ? '16px' : '13px')};
	line-height: ${({ isMobile }) => (isMobile ? '24px' : '20px')};
	background-color: ${({ theme }) => theme.gray[100]};
	color: ${({ theme }) => theme.gray[1000]};
	border-radius: 18px;
	border-bottom-right-radius: 0px;
`;

export const SystemMessage = styled.div<MessageProps>`
	// layout
	padding-left: 10px;
	padding-right: 10px;
	padding-top: 8px;
	margin-left: 8px;
	width: calc(100% - 50px);

	// style
	color: ${({ theme }) => theme.gray[1000]};
	font-size: ${({ isMobile }) => (isMobile ? '16px' : '13px')};
	line-height: ${({ isMobile }) => (isMobile ? '24px' : '20px')};
	user-select: text;
	cursor: text;
`;

export const ThumbnailScroller = styled.div`
	// layout
	display: flex;
	overflow-x: auto;
	overflow-y: hidden;
	scrollbar-width: thin;
	gap: 8px;

	&::-webkit-scrollbar {
		height: 6px;
	}

	// ensure borders of thumbnails don't get clipped
	padding-top: 1px;
	// a few extra pixels at the bottom make the scrollbar
	// spacing look right when a mouse is plugged in
	padding-bottom: 4px;

	// styling
	-webkit-overflow-scrolling: touch;
	scrollbar-color: ${({ theme }) => theme.gray[400]} ${({ theme }) => theme.gray[100]};
`;

export const Thumbnails = styled(ThumbnailScroller)`
	margin-bottom: 12px;
`;

export const Frames = styled(ThumbnailScroller)`
	margin-top: 12px;
	margin-bottom: 16px;
	border-radius: 8px;
	cursor: default;
	user-select: none;
`;

export const Attachments = styled(ThumbnailScroller)`
	// layout
	gap: 12px;
	padding-top: 1px;
	margin-bottom: 4px;
`;

interface BaseProps {
	corner: Point;
	selected: boolean;
}

export const SelectionButton = styled.div<BaseProps>`
	//layout
	position: fixed;
	left: ${(props) => props.corner.x}px;
	top: ${(props) => props.corner.y}px;
	// transform: translate(-50%, -100%);
	// margin-top: -10px;
	z-index: 1000;
	border: 1px solid ${({ theme }) => theme.gray[600]};
	background-color: ${({ theme, selected }) => (selected ? theme.blue[500] : theme.gray[0])};
	width: 28px;
	height: 28px;
	border-radius: 6px;
	display: flex;
	align-items: center;
	justify-content: center;
	&:hover {
		background-color: ${({ theme, selected }) => (selected ? theme.blue[500] : theme.gray[200])};
	}
`;

export const DragOverlay = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: ${({ theme }) => theme.blue[300]}AA;
	border: 2px dashed ${({ theme }) => theme.blue[500]};
	display: flex;
	align-items: center;
	justify-content: center;
	pointer-events: none;
	z-index: 1000;
	transition: opacity 0.2s ease-in-out;
`;

export const DragOverlayContent = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;
`;

export const DragOverlayText = styled.div<MessageProps>`
	color: ${({ theme }) => theme.gray[1000]};
	font-size: ${({ isMobile }) => (isMobile ? '16px' : '14px')};
	font-weight: 500;
`;

export const StatusMessage = styled.div<MessageProps>`
	display: flex;
	align-items: center;
	gap: 12px;
	padding-top: 10px;
	padding-bottom: 12px;
	padding-left: 14px;
	padding-right: 16px;
	margin: 8px 16px;
	margin-bottom: 20px;
	background-color: ${({ theme }) => theme.gray[100]};
	border: 1px solid ${({ theme }) => theme.gray[200]};
	border-radius: 8px;
	font-size: ${({ isMobile }) => (isMobile ? '16px' : '13px')};
	color: ${({ theme }) => theme.gray[800]};
	animation: backgroundPulse 4s ease-in-out infinite;

	@keyframes backgroundPulse {
		0% {
			background-color: ${({ theme }) => theme.gray[100]};
		}
		50% {
			background-color: ${({ theme }) => theme.gray[200]};
		}
		100% {
			background-color: ${({ theme }) => theme.gray[100]};
		}
	}
`;

export const StatusText = styled.div`
	flex: 1;
	line-height: 1.4;
`;
